import { Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { GeoTargetType } from "form/components/content/geo-targets/GeoTargetType"
import { GEO_TARGETS_TYPES, IFormGeoTargets, IGeoTargetsListItem } from "form/model"
import { getDistanceUnit } from "form/tools/geo-targets"
import { getRadiusInUserUnit } from "form/tools/geo-targets-coordinates"
import { getGeoTargetListItems } from "form/tools/geo-targets-list"
import React from "react"
import { IGeoShape } from "shape/model"
import { getShapeTitle } from "shape/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { getDataGridTexts } from "ui/tools/list"

interface ICampaignGeoTargetsSummaryProps {
  geoTargets: IFormGeoTargets
  contentWidth: number
}

export const CampaignGeoTargetsSummary = React.memo(({ geoTargets, contentWidth }: ICampaignGeoTargetsSummaryProps) => {
  const items: IGeoTargetsListItem[] = getGeoTargetListItems(geoTargets)
  if (items.length === 0) {
    return (
      <Typography id="summary-geo-targets-empty" color="textSecondary" fontSize=".85rem" sx={{ ml: "1rem" }}>
        {translate("label.everywhere")}
      </Typography>
    )
  }
  if (contentWidth < 720) {
    return (
      <Typography id="summary-geo-targets-count" color="textPrimary" fontSize=".85rem" sx={{ ml: "1rem" }}>
        {translate("label.geo-targets.count", items.length)}
      </Typography>
    )
  }
  const distanceUnit = translate(`label.distance-unit.${getDistanceUnit()}`)
  const columns: Array<GridColDef<IGeoTargetsListItem>> = [
    {
      field: "type",
      headerName: translate("label.type"),
      flex: 2,
      renderCell: params => <GeoTargetType type={params.row.type as GEO_TARGETS_TYPES} />
    },
    {
      field: "name",
      headerName: translate("label.name"),
      minWidth: 170,
      flex: 2,
      renderCell: params =>
        params.row.type === GEO_TARGETS_TYPES.COORDINATE
          ? params.row.name
          : getShapeTitle({ shape: params.row.shape as IGeoShape })
    },
    {
      field: "radius",
      headerName: `${translate("label.radius")} (${distanceUnit})`,
      flex: 1,
      renderCell: params =>
        typeof params.row.radius === "number"
          ? getRadiusInUserUnit(params.row.radius)
          : params.row.type === GEO_TARGETS_TYPES.COORDINATE
            ? translate("label.n/a")
            : ""
    },
    {
      field: "longitude",
      headerName: translate("label.longitude"),
      flex: 1,
      renderCell: params =>
        typeof params.row.longitude === "number"
          ? params.row.longitude.toFixed(6)
          : typeof params.row.longitude === "string" && params.row.longitude !== ""
            ? Number(params.row.longitude).toFixed(6)
            : ""
    },
    {
      field: "latitude",
      headerName: translate("label.latitude"),
      flex: 1,
      renderCell: params =>
        typeof params.row.latitude === "number"
          ? params.row.latitude.toFixed(6)
          : typeof params.row.latitude === "string" && params.row.latitude !== ""
            ? Number(params.row.latitude).toFixed(6)
            : ""
    }
  ]
  return (
    <FlexBox
      id="summary-geo-targets-list"
      vertical
      fullWidth
      sx={{
        "minWidth": "100%",
        "maxWidth": "100%",
        "height": "15rem",
        "minHeight": "15rem",
        "maxHeight": "15rem",
        "transition": "all 400ms ease",
        "& > *": {
          width: "100%"
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: "hidden"
        },
        "& .MuiDataGrid-root": {
          border: "none"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
          {
            outline: "none !important"
          }
      }}>
      <DataGrid
        rows={items}
        columns={columns}
        disableColumnFilter
        disableColumnMenu
        className="geo-targets-summary-list"
        rowHeight={30}
        autoPageSize
        localeText={getDataGridTexts()}
      />
    </FlexBox>
  )
})
